'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { H2, P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { usePathname } from '@/i18n/navigation';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

const GreyListedCountryPopup = ({
  isGreyListedCountry,
  country
}: {
  isGreyListedCountry: boolean;
  country: string;
}) => {
  const t = useTranslations('locationCheck');
  const pathname = usePathname();
  const termsPath = C_Routes[E_Routes.TERMS_AND_CONDITIONS].link;

  const [isOpen, setIsOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const parentRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const accepted = localStorage.getItem('grayListedCountryAccepted');

    const isTerms = pathname.startsWith(termsPath);

    if (isGreyListedCountry && !accepted && !isTerms) {
      setIsOpen(true);
    }
  }, [isGreyListedCountry, pathname, termsPath]);

  useEffect(() => {
    if (!isOpen) return;

    const accepted = localStorage.getItem('grayListedCountryAccepted');
    if (!isGreyListedCountry || accepted) return;

    const ensureAttached = () => {
      const popup = popupRef.current;
      if (!popup) return;

      if (!parentRef.current) {
        parentRef.current = popup.parentElement;
      }

      if (!popup.isConnected) {
        const parent = parentRef.current || document.body;
        parent.appendChild(popup);
      }
    };

    ensureAttached();
    const observer = new MutationObserver(ensureAttached);
    observer.observe(document.body, { childList: true });

    return () => observer.disconnect();
  }, [isOpen, isGreyListedCountry, pathname]);

  useEffect(() => {
    const accepted = localStorage.getItem('grayListedCountryAccepted');
    const isTerms = pathname.startsWith(termsPath);

    if (!isGreyListedCountry || accepted || !isTerms) return;

    const openOnAction = (event: Event) => {
      if (isOpen) return;
      const target = event.target as Element | null;
      if (target?.closest('main')) return;
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(true);
    };

    document.addEventListener('click', openOnAction, true);

    return () => {
      document.removeEventListener('click', openOnAction, true);
    };
  }, [isGreyListedCountry, isOpen, pathname, termsPath]);

  const handleAccept = () => {
    if (!isChecked) return;
    localStorage.setItem('grayListedCountryAccepted', 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={popupRef}
      data-grey-listed-country-popup="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/50 backdrop-blur-sm"
    >
      <div
        className="relative w-full max-w-115 mx-4 rounded-3xl p-[1px]"
        style={{
          background: 'linear-gradient(0deg, #27272A 0%, #FAFAFA 100%)'
        }}
      >
        <div className="relative w-full p-7 flex flex-col gap-6 bg-[image:var(--gradient-black-to-gray)] rounded-3xl items-center max-h-[calc(90vh-150px)] overflow-auto">
          <H2 className="text-2xl font-bold text-foreground text-center">
            {t('title')}
          </H2>
          <div className="flex flex-col gap-4 text-foreground">
            <div className="flex flex-col items-center">
              <P className="text-sm leading-relaxed font-bold text-center">
                {t('greeting')}
              </P>
              <P className="text-sm leading-relaxed font-bold text-center">
                {t('unavailable', { country })}
              </P>
            </div>
            <P className="text-sm leading-relaxed font-bold text-center">
              {t('confirmPrompt', { country })}
            </P>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex items-center gap-4 px-4 py-3.5 border-1 border-border rounded-lg bg-transparent">
              <Checkbox
                id="location-confirm"
                checked={isChecked}
                onCheckedChange={checked => setIsChecked(checked === true)}
                className="mt-0.5 bg-secondary border-border cursor-pointer"
              />
              <label
                htmlFor="location-confirm"
                className="text-xs font-normal leading-tight cursor-pointer text-foreground flex-1"
              >
                <P className="flex text-sm font-normal">
                  <span>
                    {t.rich('restrictedJurisdictionConfirmLabel', {
                      country,
                      restrictedJurisdiction: txt => (
                        <a
                          href={termsPath}
                          className="underline block"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {txt}
                        </a>
                      )
                    })}
                  </span>
                </P>
              </label>
            </div>
          </div>

          <Button
            onClick={handleAccept}
            disabled={!isChecked}
            variant="default"
            className="w-full"
          >
            {t('continue')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GreyListedCountryPopup;
